import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { LogIn, ArrowLeft, Check, Building2, Loader2, HardHat, Mail, BarChart3, HelpCircle } from 'lucide-react';
import axios from 'axios';
import { isMobileDevice } from '../hooks/useIsMobile';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';


const Login = () => {
  const { login, loginV2, selectTenant, pendingTenantSelection, tenants: authTenants } = useAuth();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const previousThemeRef = useRef(theme);

  // Auto-redirect mobile devices to the mobile portal
  useEffect(() => {
    if (isMobileDevice()) {
      navigate('/mobile/login', { replace: true });
    }
  }, [navigate]);

  const [loading, setLoading] = useState(false);
  
  // V2 Auth state - Default to email login when enabled
  const [useEmailLogin, setUseEmailLogin] = useState(true); // Default to email login
  const [emailLoginEnabled, setEmailLoginEnabled] = useState(false);
  const [showTenantSelector, setShowTenantSelector] = useState(false);
  const [v2Tenants, setV2Tenants] = useState([]);

  // Login form (legacy)
  const [loginForm, setLoginForm] = useState({
    clientId: '',
    username: '',
    password: ''
  });
  
  // V2 Login form (email-based)
  const [emailLoginForm, setEmailLoginForm] = useState({
    email: '',
    password: ''
  });

  

  // Force dark mode on login/signup pages
  useEffect(() => {
    previousThemeRef.current = theme;
    if (theme !== 'dark') {
      setTheme('dark');
    }
    // Restore previous theme when leaving the page
    return () => {
      if (previousThemeRef.current !== 'dark') {
        setTheme(previousThemeRef.current);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Check if email login (v2 auth) is enabled
  useEffect(() => {
    const checkEmailLoginEnabled = async () => {
      try {
        // Use public endpoint first — does not require authentication
        const publicResponse = await axios.get(`${API_URL}/auth/feature-flags`);
        setEmailLoginEnabled(publicResponse.data.new_auth_enabled || false);
      } catch {
        // Feature flags not available, keep email login disabled
        setEmailLoginEnabled(false);
      }
    };
    checkEmailLoginEnabled();
  }, []);
  
  // Helper — desktop login should hide tenants where the user only has the
  // 'operator' role (those are mobile-only worker accounts and belong on the
  // /mobile login). A user with BOTH admin/dispatcher AND operator (e.g.
  // demo@equipr.com.au which doubles as a worker on demo tenants) should
  // still appear in the desktop tenant picker.
  const isDesktopAccessibleTenant = (t) => {
    const roles = Array.isArray(t?.roles) && t.roles.length > 0 ? t.roles : [t?.role];
    return roles.some((r) => r && r !== 'operator');
  };

  // Handle pending tenant selection from context
  useEffect(() => {
    if (pendingTenantSelection && authTenants?.length > 0) {
      // Filter to only show tenant user accounts (admin/dispatcher), not pure-operator accounts
      const tenantUserAccounts = authTenants.filter(isDesktopAccessibleTenant);
      if (tenantUserAccounts.length > 1) {
        setV2Tenants(tenantUserAccounts);
        setShowTenantSelector(true);
      }
    }
  }, [pendingTenantSelection, authTenants]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginForm.clientId.toUpperCase(), loginForm.username, loginForm.password);
      toast.success('Welcome back!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };
  
  // V2 Email Login Handler
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await loginV2(emailLoginForm.email, emailLoginForm.password);
      
      // Check if backend requires tenant selection (multiple active tenants)
      if (result.requiresTenantSelection) {
        // Filter out pure-operator entries (those go through /mobile). A
        // tenant with admin+operator both should still appear here.
        const tenantUserAccounts = result.tenants?.filter(isDesktopAccessibleTenant) || [];
        
        if (tenantUserAccounts.length > 1) {
          // User has multiple tenant user accounts - show selector
          setV2Tenants(tenantUserAccounts);
          setShowTenantSelector(true);
          toast.info('Please select your organization');
        } else if (tenantUserAccounts.length === 1) {
          // Only one non-operator tenant - auto-select it
          const singleTenant = tenantUserAccounts[0];
          try {
            await selectTenant(singleTenant.client_id);
            toast.success(`Welcome back!`);
            navigate('/dashboard');
          } catch (selectError) {
            console.error('Auto-select tenant failed:', selectError);
            // Fall back to showing the tenant picker so the user can retry
            setV2Tenants(tenantUserAccounts);
            setShowTenantSelector(true);
            toast.error('Could not auto-select organization. Please select manually.');
          }
        } else if (result.tenants?.length > 0) {
          // User only has operator accounts, no tenant user accounts
          toast.error('No tenant accounts found. Please use the Worker Login for mobile access.');
        }
      } else {
        // Single tenant was auto-selected by backend OR user has default tenant
        // Navigate directly to dashboard - no need to call selectTenant again
        // Use navigate instead of window.location.href to avoid full page reload
        toast.success('Welcome back!');
        navigate('/dashboard');
      }
    } catch (error) {
      const errorDetail = error.response?.data?.detail || 'Login failed';
      if (errorDetail.includes('not yet enabled')) {
        toast.error('Email login is not yet available. Please use Client ID login.');
        setUseEmailLogin(false);
      } else {
        toast.error(errorDetail);
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Tenant Selection Handler
  const handleSelectUserTenant = async (tenant) => {
    setLoading(true);
    try {
      await selectTenant(tenant.client_id);
      setShowTenantSelector(false);
      toast.success(`Signed in to ${tenant.company_name}`);
      // Use React Router navigate to avoid full page reload
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to select organization');
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 bg-cover bg-center relative"
      style={{
        backgroundImage: 'url(https://customer-assets.emergentagent.com/job_job-tracker-app-6/artifacts/penuw891_iStock-1174890965.jpg)',
      }}
    >
      <div className="absolute inset-0 bg-black/60" />
      
      {/* Login Card */}
      {!showTenantSelector && (
        <Card className="w-full max-w-md relative z-10 border-0 shadow-2xl" data-testid="login-card">
          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-xl overflow-hidden">
                <img src="/equipr-logo.png" alt="EQUIPR" className="w-full h-full object-cover" />
              </div>
            </div>
            <CardTitle className="font-heading text-2xl">EQUIPR CORE</CardTitle>
            <CardDescription>Run your business from the CORE</CardDescription>
          </CardHeader>
          
          <CardContent>
            {/* V2 Email Login Form - Default method */}
            {emailLoginEnabled && useEmailLogin && (
              <form onSubmit={handleEmailLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label className="label-overline">Email</Label>
                  <Input
                    data-testid="email-login-email"
                    type="email"
                    value={emailLoginForm.email}
                    onChange={(e) => setEmailLoginForm({ ...emailLoginForm, email: e.target.value })}
                    placeholder="you@company.com"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label className="label-overline">Password</Label>
                  <Input
                    data-testid="email-login-password"
                    type="password"
                    value={emailLoginForm.password}
                    onChange={(e) => setEmailLoginForm({ ...emailLoginForm, password: e.target.value })}
                    placeholder="********"
                    required
                  />
                  <div className="flex justify-end">
                    <Link
                      to="/v2/forgot-password"
                      className="text-xs text-muted-foreground hover:text-[#ffd108] transition-colors"
                      data-testid="email-forgot-password-link"
                    >
                      Forgot password?
                    </Link>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full btn-volt" 
                  disabled={loading}
                  data-testid="email-login-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4 mr-2" />
                      Sign In with Email
                    </>
                  )}
                </Button>

                <div className="text-center pt-2">
                  <button
                    type="button"
                    onClick={() => setUseEmailLogin(false)}
                    className="text-xs text-muted-foreground hover:text-white transition-colors inline-flex items-center gap-1"
                    data-testid="switch-to-client-id"
                  >
                    <LogIn className="h-3 w-3" />
                    Sign in with Client ID
                  </button>
                </div>
              </form>
            )}
            {(!emailLoginEnabled || !useEmailLogin) && (
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label className="label-overline">Client ID</Label>
                  <Input
                    data-testid="client-id"
                    value={loginForm.clientId}
                    onChange={(e) => setLoginForm({ ...loginForm, clientId: e.target.value.toUpperCase() })}
                    placeholder="ABC001"
                    maxLength={6}
                    className="font-mono uppercase"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label className="label-overline">Username</Label>
                  <Input
                    data-testid="username"
                    value={loginForm.username}
                    onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                    placeholder="Your username"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label className="label-overline">Password</Label>
                  <Input
                    data-testid="password"
                    type="password"
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    placeholder="********"
                    required
                  />
                  <div className="flex justify-end">
                    <Link
                      to="/v2/forgot-password"
                      className="text-xs text-muted-foreground hover:text-[#ffd108] transition-colors"
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </Link>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full btn-volt" 
                  disabled={loading}
                  data-testid="login-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    <>
                      <LogIn className="h-4 w-4 mr-2" />
                      Sign In
                    </>
                  )}
                </Button>

                {emailLoginEnabled && (
                  <div className="text-center pt-2">
                    <button
                      type="button"
                      onClick={() => setUseEmailLogin(true)}
                      className="text-xs text-muted-foreground hover:text-white transition-colors inline-flex items-center gap-1"
                      data-testid="switch-to-email"
                    >
                      <Mail className="h-3 w-3" />
                      Sign in with Email
                    </button>
                  </div>
                )}
              </form>
            )}

            <div className="mt-6 pt-4 border-t text-center space-y-3">
              <Button
                className="w-full btn-volt"
                onClick={() => navigate('/free-trial')}
                data-testid="signup-button"
              >
                <HardHat className="h-4 w-4 mr-2" />
                SIGN UP
              </Button>

              <a
                href="/getting-started/"
                target="_blank"
                rel="noopener noreferrer"
                className="block text-xs text-muted-foreground hover:text-[#ffd108] transition-colors"
                data-testid="getting-started-guide-link"
              >
                New here? View the Getting Started guide →
              </a>
              
              <div className="flex items-center justify-center gap-3">
                <Link
                  to="/features"
                  className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="view-features-link"
                >
                  <BarChart3 className="h-4 w-4" />
                  View all features
                </Link>
                {/* Support plans link temporarily hidden — re-enable soon
                <span className="text-muted-foreground text-xs">|</span>
                <Link
                  to="/support-plans"
                  className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="view-support-link"
                >
                  <HelpCircle className="h-4 w-4" />
                  Support plans
                </Link>
                */}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Tenant Selector - Shown when user has multiple tenants */}
      {showTenantSelector && (
        <Card className="w-full max-w-md relative z-10 border-0 shadow-2xl" data-testid="tenant-selector-card">
          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-xl overflow-hidden">
                <img src="/equipr-logo.png" alt="EQUIPR" className="w-full h-full object-cover" />
              </div>
            </div>
            <CardTitle className="font-heading text-2xl">Select Organization</CardTitle>
            <CardDescription>You have access to multiple organizations</CardDescription>
          </CardHeader>
          
          <CardContent>
            <div className="space-y-3">
              {(() => {
                // Defensive dedupe by client_id + role — fixes duplicate
                // entries caused by re-running seed scripts that $push to
                // unified_users.tenant_access without checking for existing.
                const seen = new Set();
                const dedup = (v2Tenants || []).filter((t) => {
                  const k = `${t.client_id}::${t.role}`;
                  if (seen.has(k)) return false;
                  seen.add(k);
                  return true;
                });
                return dedup.map((tenant) => (
                <button
                  key={`${tenant.client_id}-${tenant.role}`}
                  onClick={() => handleSelectUserTenant(tenant)}
                  disabled={loading}
                  className="w-full p-4 rounded-lg border border-zinc-700 hover:border-[#ffd108] hover:bg-zinc-800/50 transition-all text-left group"
                  data-testid={`tenant-option-${tenant.client_id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-zinc-700 flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-zinc-400 group-hover:text-[#ffd108]" />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-white">{tenant.company_name}</div>
                      <div className="text-xs text-zinc-400">
                        {tenant.client_id} • {tenant.role}
                        {tenant.is_operator && ' • Operator'}
                      </div>
                    </div>
                    {loading && (
                      <Loader2 className="h-4 w-4 animate-spin text-[#ffd108]" />
                    )}
                  </div>
                </button>
                ));
              })()}
            </div>
            
            <div className="mt-6 pt-4 border-t text-center">
              <button
                onClick={() => {
                  setShowTenantSelector(false);
                  setUseEmailLogin(true);
                }}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                data-testid="back-to-login"
              >
                <ArrowLeft className="h-4 w-4 inline mr-1" />
                Sign in with different account
              </button>
            </div>
          </CardContent>
        </Card>
      )}


    </div>
  );
};

export default Login;
